import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { ChevronDown } from 'lucide-react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import PaginationComponent from '../../shared/components/PaginationComponent';
import NoDataFound from '../components/layout/NoDataFound';
import Search from '../../shared/components/ui/Search';
import {
  getAccountsPriorities,
  updateAccountsPriorities,
  getLandById,
  payManagementTaskExpense,
  getInvoiceByDealId,
  getSiteWalletApprovals,
  getSiteWalletReimbursements
} from '../services/api/implementation/impl';
import Loader from '../../shared/components/ui/Loader';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import FormInput from '../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import AccountsKycPanel from '../components/postSales/AccountsKycPanel';
import {
  evaluateKycVerification,
  kycVerifyBadgeClasses
} from '../components/postSales/postSalesKyc';
import { useSmartBack } from '../../shared/navigation';
import { useSiteWalletRole } from '../hooks/useSiteWalletAccess';
import ExpenseApprovalsPanel from './SiteWallet/ExpenseApprovalsPanel';
import ReimbursementsPanel from './SiteWallet/ReimbursementsPanel';

const Priorities = () => {
  const queryClient = useQueryClient();
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/dashboard' });
  const { projects, projectsForDropdown } = useProjectsList();

  // State management
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [kycPanelDeal, setKycPanelDeal] = useState(null);

  // Filter states
  const [projectFilter, setProjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown states
  const [openDropdown, setOpenDropdown] = useState(null);
  const projectHeaderRef = useRef(null);
  const statusHeaderRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.get('tab') === 'wallet-approvals'
      ? 'Wallet Expense Approvals'
      : searchParams.get('tab') === 'wallet-reimbursements'
        ? 'Wallet Reimbursements'
        : 'All Payment'
  );
  // Site Wallet queues live here beside the other payment work; the panels
  // bring their own data, so the priorities list isn't fetched for them.
  const walletRole = useSiteWalletRole();
  const isWalletTab = activeTab === 'Wallet Expense Approvals' || activeTab === 'Wallet Reimbursements';
  const walletApprovals = useQuery({
    queryKey: ['siteWalletApprovals'],
    queryFn: getSiteWalletApprovals,
    enabled: walletRole.isStaff,
    staleTime: 60 * 1000
  });
  const walletWaiting = walletApprovals.data?.length ?? 0;
  // Same query as the Reimbursements panel, so paying one updates the count.
  const walletDue = useQuery({
    queryKey: ['siteWalletReimbursements'],
    queryFn: () => getSiteWalletReimbursements(),
    enabled: walletRole.isStaff,
    staleTime: 60 * 1000
  });
  // Items still to pay: own-pocket expenses due, plus settlement payouts.
  const walletToPay =
    (walletDue.data?.by_holder ?? []).reduce((n, g) => n + (g.expenses?.length ?? 0), 0) +
    (walletDue.data?.settlement_payouts?.length ?? 0);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const {
    data: prioritiesData,
    refetch: refetchPriorities,
    isLoading,
    isPending,
    isFetching
  } = useQuery({
    queryKey: ['orderActivities', page, projectFilter, statusFilter, searchQuery, activeTab],
    queryFn: () => getAccountsPriorities(
      page,
      limit,
      projectFilter,
      statusFilter,
      searchQuery,
      activeTab === 'Management Task Expense' ? 'Management Task Expense' : ''
    ),
    placeholderData: keepPreviousData,
    enabled: !isWalletTab
  });

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Mutation for updating priority status
  const { mutate: updatePriorityStatus, isPending: isGeneratingInvoice } = useMutation({
    mutationFn: (payload) => updateAccountsPriorities(selectedPriority?.id, payload),
    onSuccess: async (result) => {
      const invoice = result?.data ?? result;
      const invoiceId = invoice?.id;
      const did = selectedPriority?.deal?.id;
      if (!invoiceId) {
        toast.error('Invoice was not created. Refresh and try again.');
        return;
      }
      toast.success('Invoice generated successfully');
      if (did != null && did !== '') {
        queryClient.invalidateQueries({ queryKey: ['prioritiesGenerateInvoiceModal', did] });
        queryClient.invalidateQueries({ queryKey: ['orderActivities', String(did)] });
        queryClient.invalidateQueries({ queryKey: ['orderActivities', did] });
      }
      await refetchPriorities();
      handleCloseInvoiceModal();
    },
    onError: (error) => {
      console.error('Error generating invoice:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to generate invoice');
    }
  });

  const { mutate: payExpense, isPending: isPayingExpense } = useMutation({
    mutationFn: (id) => payManagementTaskExpense(id),
    onSuccess: async () => {
      toast.success('Expense paid successfully');
      await refetchPriorities();
    },
    onError: (error) => {
      console.error('Error paying expense:', error);
      toast.error(error?.response?.data?.message || 'Failed to pay expense');
    }
  });

  // Form handling for invoice modal
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm();

  const invoiceType = watch('invoiceType');
  const [landOwners, setLandOwners] = useState([]);
  const [loadingLandOwners, setLoadingLandOwners] = useState(false);

  useEffect(() => {
    const fetchLandDetails = async () => {
      if (invoiceType === 'Owner Based' && selectedPriority?.deal?.unit?.block?.project?.land_id) {
        try {
          setLoadingLandOwners(true);
          const landId = selectedPriority.deal.unit.block.project.land_id;
          const landData = await getLandById(landId);

          if (landData?.ownerLandMappings?.length) {
            const owners = landData.ownerLandMappings.map(mapping => ({
              label: mapping.owner?.name || 'Unknown Owner',
              value: mapping.owner?.id
            }));
            setLandOwners(owners);
          } else {
            setLandOwners([]);
          }
        } catch (error) {
          console.error('Error fetching land details:', error);
          toast.error('Failed to fetch land owners');
        } finally {
          setLoadingLandOwners(false);
        }
      } else {
        setLandOwners([]);
        setValue('landOwner', '');
      }
    };

    fetchLandDetails();
  }, [invoiceType, selectedPriority, setValue]);

  const modalDealId = selectedPriority?.deal?.id;

  const {
    data: dealInvoicesResponse,
    isLoading: isLoadingDealInvoices,
    isError: isDealInvoicesError
  } = useQuery({
    queryKey: ['prioritiesGenerateInvoiceModal', modalDealId],
    queryFn: () => getInvoiceByDealId(modalDealId, 1, 200, 'none'),
    enabled: isInvoiceModalOpen && modalDealId != null && modalDealId !== ''
  });

  const previousInvoices = Array.isArray(dealInvoicesResponse?.data)
    ? dealInvoicesResponse.data
    : [];

  // Handle priority action button click - open modal
  const handleGenerateInvoice = (priority) => {
    setSelectedPriority(priority);
    setIsInvoiceModalOpen(true);
  };

  // Close invoice modal
  const handleCloseInvoiceModal = () => {
    setIsInvoiceModalOpen(false);
    setSelectedPriority(null);
    setLandOwners([]);
    reset();
  };

  // Handle invoice form submission
  const onSubmitInvoice = async (data) => {
    const isOwnerBased = data.invoiceType === 'Owner Based';

    const payload = {
      invoiceNumber: data.invoiceNumber,
      issueDate: data.issueDate,
      companyBased: !isOwnerBased
    };

    if (isOwnerBased) {
      if (!data.landOwner) {
        toast.error('Please select a land owner');
        return;
      }
      payload.land_owner_id = data.landOwner;
    }

    console.log('Invoice Payload:', payload);

    updatePriorityStatus(payload);
  };

  // Search handler
  const handleSearchChange = useCallback((incomingQuery) => {
    setPage(1);
    setSearchQuery(incomingQuery);
  }, []);

  const handleOnClickSearch = async () => {
    await refetchPriorities();
  };

  // Track page view
  useEffect(() => {
    mixpanel.track('Priorities Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Status options for filter
  const statusOptions = [
    { id: 'Completed', label: 'Completed', value: 'Completed' },
    { id: 'Pending', label: 'Pending', value: 'Pending' }
  ];

  // Handler to open dropdown and set position
  const handleHeaderDropdownClick = (type, event) => {
    event.preventDefault();
    event.stopPropagation();

    // If clicking the same dropdown that's already open, close it
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }

    setOpenDropdown(type);
  };

  // Handler to select item
  const handleSelectDropdownItem = (type, value) => {
    setOpenDropdown(null);
    setPage(1);

    if (type === 'project') {
      if (!value) {
        setProjectFilter('');
      } else {
        setProjectFilter(value);
      }
    } else if (type === 'status') {
      if (!value) {
        setStatusFilter('');
      } else {
        setStatusFilter(value);
      }
    }
  };

  // Add a global click handler to close dropdown when clicking outside
  useEffect(() => {
    if (!openDropdown) return;

    const handleClick = (event) => {
      // Don't close if clicking inside a dropdown or on dropdown trigger
      if (
        event.target.closest('[data-dropdown-trigger]') ||
        event.target.closest('[data-dropdown-panel]') ||
        event.target.closest('.absolute.bg-white.border.rounded.shadow')
      ) {
        return;
      }
      setOpenDropdown(null);
    };

    // Use capture phase to ensure it runs before other click handlers
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [openDropdown]);

  const displayData = useMemo(() => {
    if (!prioritiesData?.data) return [];

    // For Management Task Expense, data is already filtered by API
    if (activeTab === 'Management Task Expense') {
      return prioritiesData.data;
    }

    // For All Payment, we fetch all and exclude Management Task Expense
    return prioritiesData.data.filter(item => item.stageName !== 'Management Task Expense');
  }, [prioritiesData?.data, activeTab]);

  const dealGroups = useMemo(() => {
    if (!displayData.length) return [];
    const map = new Map();
    for (const p of displayData) {
      const deal = p.deal;
      const numericId = deal?.id;
      const dealKey = numericId != null ? String(numericId) : `no-deal-${p.id}`;
      if (!map.has(dealKey)) {
        map.set(dealKey, {
          dealKey,
          numericDealId: numericId ?? null,
          deal,
          priorities: []
        });
      }
      map.get(dealKey).priorities.push(p);
    }
    return Array.from(map.values());
  }, [displayData]);

  const [expandedDeals, setExpandedDeals] = useState({});

  const toggleDealExpanded = (dealKey) => {
    setExpandedDeals((prev) => ({ ...prev, [dealKey]: !prev[dealKey] }));
  };

  useEffect(() => {
    setExpandedDeals({});
  }, [page, projectFilter, statusFilter, searchQuery, activeTab]);

  const totalPages = Math.ceil((prioritiesData?.meta?.total || 0) / limit);

  // Avoid empty-state flash while filters/page/tab change refetches (isPending is often false then).
  const showListLoadingPlaceholder =
    displayData.length === 0 && (isPending || isFetching);

  const allPaymentFiltersBar = (
    <div className="sticky top-0 z-20 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex min-w-0 flex-wrap items-end gap-5">
        <div className="relative flex min-w-[10rem] flex-col gap-1" ref={projectHeaderRef}>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Project
          </span>
          <button
            type="button"
            className="inline-flex h-9 min-w-[11rem] max-w-[16rem] items-center justify-between gap-2 rounded-md border border-gray-300 bg-gray-50/80 px-3 text-left text-sm text-gray-800 shadow-sm hover:border-[#346C75]/40 hover:bg-white"
            data-dropdown-trigger
            onClick={(e) => {
              e.stopPropagation();
              handleHeaderDropdownClick('project', e);
            }}>
            <span className="truncate">
              {projectFilter
                ? projectsForDropdown.find((p) => String(p.id) === String(projectFilter))?.label ||
                  'Project'
                : 'All projects'}
            </span>
            <ChevronDown size={16} className="shrink-0 text-gray-500" />
          </button>
          {openDropdown === 'project' && (
            <div
              data-dropdown-panel
              className="absolute left-0 top-full z-[1000] mt-1 min-w-[12rem] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
              style={{ maxHeight: '250px' }}
              onClick={(e) => e.stopPropagation()}>
              {[
                {
                  id: '',
                  value: '',
                  label: projectFilter ? 'Clear filter' : 'Select project',
                  selected: true
                },
                ...projectsForDropdown
              ].map((proj, idx) => (
                <div
                  key={proj.id || idx}
                  className={`cursor-pointer px-4 py-2 text-sm hover:bg-gray-50 ${proj.id === '' && 'bg-gray-50'} ${
                    String(proj.id) === String(projectFilter) && 'bg-teal-50 text-[#346C75]'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectDropdownItem('project', proj.id);
                  }}>
                  {proj.label}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="relative flex flex-col gap-1" ref={statusHeaderRef}>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Status
          </span>
          <button
            type="button"
            className="inline-flex h-9 min-w-[8.5rem] items-center justify-between gap-2 rounded-md border border-gray-300 bg-gray-50/80 px-3 text-left text-sm text-gray-800 shadow-sm hover:border-[#346C75]/40 hover:bg-white"
            data-dropdown-trigger
            onClick={(e) => {
              e.stopPropagation();
              handleHeaderDropdownClick('status', e);
            }}>
            <span>{statusFilter || 'All'}</span>
            <ChevronDown size={16} className="shrink-0 text-gray-500" />
          </button>
          {openDropdown === 'status' && (
            <div
              data-dropdown-panel
              className="absolute left-0 top-full z-[1000] mt-1 min-w-[10rem] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
              style={{ maxHeight: '250px' }}
              onClick={(e) => e.stopPropagation()}>
              {[
                {
                  id: '',
                  value: '',
                  label: statusFilter ? 'Clear filter' : 'Select status',
                  selected: true
                },
                ...statusOptions
              ].map((status, idx) => (
                <div
                  key={status.id || idx}
                  className={`cursor-pointer px-4 py-2 text-sm hover:bg-gray-50 ${status.value === '' && 'bg-gray-50'} ${
                    status.value === statusFilter && 'bg-teal-50 text-[#346C75]'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectDropdownItem('status', status.value);
                  }}>
                  {status.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Loader isVisible={isLoading} />

      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}>
          <Header data={{ name: 'Priorities' }} backArrow={true} onBack={onBack} />
          <div
            className="fixed z-[9] bg-white border-b border-gray-200 shadow-sm px-6 py-4 transition-all duration-300 ease-in-out"
            style={{
              top: '4.1rem',
              left: collapsed ? '76px' : '16.6%',
              right: '0'
            }}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <button
                  type="button"
                  onClick={() => setActiveTab('All Payment')}
                  className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'All Payment'
                    ? 'text-[#346C75] border-b-2 border-[#346C75]'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}>
                  All Payment
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('Management Task Expense')}
                  className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'Management Task Expense'
                    ? 'text-[#346C75] border-b-2 border-[#346C75]'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}>
                  Management Task Expense
                </button>
                {walletRole.isStaff &&
                  ['Wallet Expense Approvals', 'Wallet Reimbursements'].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`pb-2 text-sm font-medium transition-colors ${activeTab === tab
                        ? 'text-[#346C75] border-b-2 border-[#346C75]'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}>
                      {tab}
                      {tab === 'Wallet Expense Approvals' && walletWaiting > 0 ? ` (${walletWaiting})` : ''}
                      {tab === 'Wallet Reimbursements' && walletToPay > 0 ? ` (${walletToPay})` : ''}
                    </button>
                  ))}
              </div>
              {!isWalletTab && (
                <Search
                  onChange={handleSearchChange}
                  query={searchQuery}
                  onClick={handleOnClickSearch}
                  placeholder="Search priorities..."
                />
              )}
            </div>
          </div>
        </div>

        {/* Main content — filters stay visible for All Payment when list is empty or loading */}
        <GridWrapper width={100}>
          <div className="mt-[10rem] relative h-[calc(100vh-11rem)] overflow-auto rounded-t-lg flex flex-col items-center gap-4 w-full">
            {activeTab === 'All Payment' && allPaymentFiltersBar}
            {isWalletTab ? (
              <div className="w-full space-y-4 px-6 pb-6">
                {activeTab === 'Wallet Expense Approvals' ? (
                  <ExpenseApprovalsPanel role={walletRole} />
                ) : (
                  <ReimbursementsPanel role={walletRole} />
                )}
              </div>
            ) : activeTab === 'Management Task Expense' ? (
                <>
                  <div className="overflow-x-auto w-full relative rounded-t-lg">
                    <table className="text-left w-full ">
                      <thead className="bg-gray-200 !text-gray-600 w-full sticky top-0 z-20">
                        <tr className="font-normal h-[3rem] w-full">
                          <th className="px-4 py-2 border-b font-normal">Subtask / Case</th>
                          <th className="px-4 py-2 border-b font-normal">Description</th>
                          <th className="px-4 py-2 border-b font-normal">Amount</th>
                          <th className="px-4 py-2 border-b font-normal">Payment Link</th>
                          <th className="px-4 py-2 border-b font-normal">Note</th>
                          <th className="px-4 py-2 border-b font-normal relative" ref={statusHeaderRef}>
                            <span>Status</span>
                            <button
                              type="button"
                              className="ml-1 align-middle"
                              data-dropdown-trigger
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHeaderDropdownClick('status', e);
                              }}>
                              <ChevronDown size={16} />
                            </button>
                            {openDropdown === 'status' && (
                              <div
                                data-dropdown-panel
                                className="absolute bg-white border rounded shadow z-[1000] top-full left-0 min-w-full"
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  minWidth: '100%',
                                  maxHeight: '250px',
                                  overflowY: 'auto'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}>
                                {[
                                  {
                                    id: '',
                                    value: '',
                                    label: statusFilter ? 'Clear Filter' : 'Select Status',
                                    selected: true
                                  },
                                  ...statusOptions
                                ].map((status, idx) => (
                                  <div
                                    key={status.id || idx}
                                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${status.value === '' && 'bg-gray-100'
                                      } ${status.value === statusFilter && 'bg-blue-100'}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSelectDropdownItem('status', status.value);
                                    }}>
                                    {status.label}
                                  </div>
                                ))}
                              </div>
                            )}
                          </th>
                          <th className="px-4 py-2 border-b font-normal">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {showListLoadingPlaceholder ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="border-b bg-white py-16 text-center text-sm text-gray-500">
                              Loading priorities…
                            </td>
                          </tr>
                        ) : displayData.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="border-b bg-white p-0 align-top">
                              <NoDataFound
                                buttonText="Refresh"
                                onButtonClick={() => refetchPriorities()}
                                screenHeight={false}
                              />
                            </td>
                          </tr>
                        ) : (
                          displayData.map((priority, index) => (
                          <tr
                            key={priority.id || index}
                            className="border-b hover:bg-gray-50 bg-white">
                            {/* A court case's own expense has no subtask; name the case instead. */}
                            <td className="px-4 py-2 text-sm max-w-[200px] truncate" title={priority.subTaskExpense?.subTask?.title || priority.subTaskExpense?.task?.title}>
                              {priority.subTaskExpense?.subTask?.title ||
                                (priority.subTaskExpense?.task?.title
                                  ? `Court case · ${priority.subTaskExpense.task.title}`
                                  : '---')}
                            </td>
                            <td className="px-4 py-2 text-sm max-w-[200px] truncate" title={priority.subTaskExpense?.subTask?.description || priority.subTaskExpense?.task?.description}>
                              {priority.subTaskExpense?.subTask?.description ||
                                priority.subTaskExpense?.task?.description ||
                                '---'}
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-green-600">
                              ₹{priority.subTaskExpense?.amount?.toLocaleString() || '0'}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {priority.subTaskExpense?.payment_link ? (
                                <a
                                  href={priority.subTaskExpense.payment_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  View Link
                                </a>
                              ) : (
                                '---'
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm max-w-[200px] truncate" title={priority.subTaskExpense?.note}>
                              {priority.subTaskExpense?.note || '---'}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <div className="relative z-0">
                                <span className="flex pl-6 overflow-hidden text-sm">
                                  {priority.status || '---'}
                                </span>
                                <div
                                  className={`w-[5px] h-[100%] rounded-t-2xl rounded-r-[3rem] absolute left-[4px] top-[-2px] ${priority.status === 'Completed'
                                    ? 'bg-[#3F8D78]'
                                    : priority.status === 'Pending'
                                      ? 'bg-[#E97B37]'
                                      : priority.status === 'In Progress'
                                        ? 'bg-[#3B82F6]'
                                        : 'bg-gray-400'
                                    }`}>
                                  &nbsp;
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {priority.status === 'Pending' ? (
                                <button
                                  type="button"
                                  onClick={() => payExpense(priority.id)}
                                  disabled={isPayingExpense}
                                  className={`px-3 py-1 text-white text-xs rounded transition-all ${isPayingExpense
                                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                    : 'bg-[#2a565e] hover:bg-[#1f4148]'
                                    }`}>
                                  {isPayingExpense ? 'Processing...' : 'Pay Expense'}
                                </button>
                              ) : priority.status === 'Completed' ? (
                                <span className="px-3 py-1 text-white bg-green-600 text-xs rounded">
                                  Paid
                                </span>
                              ) : (
                                <span className="text-gray-500 text-xs">No actions</span>
                              )}
                            </td>
                          </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <PaginationComponent
                      page={page}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      isFetching={isLoading}
                    />
                  )}
                </>
              ) : (
                <>
                  {showListLoadingPlaceholder ? (
                    <div
                      className="flex min-h-[32vh] w-full flex-1 items-start justify-center px-4 pt-8"
                      aria-busy="true"
                      aria-label="Loading priorities"
                    />
                  ) : displayData.length > 0 ? (
                  <>
                  <div className="w-full flex flex-col gap-2 px-1 pb-4">
                    {dealGroups.map((group) => {
                      const { dealKey, numericDealId, deal, priorities } = group;
                      const open = !!expandedDeals[dealKey];
                      const dealProject = deal?.unit?.block?.project?.name || '---';
                      const leadName = deal?.lead?.user?.name || '---';
                      const blockUnit =
                        deal?.unit?.block?.name && deal?.unit?.unitName
                          ? `${deal.unit.block.name} - ${deal.unit.unitName}`
                          : '---';
                      const pendingStages = priorities.filter((p) => p.status === 'Pending').length;
                      const dealTotalRaw =
                        deal?.total_price != null && deal?.total_price !== ''
                          ? Number(deal.total_price)
                          : NaN;
                      const dealTotal = Number.isFinite(dealTotalRaw)
                        ? new Intl.NumberFormat('en-IN').format(dealTotalRaw)
                        : null;
                      const kycStatus =
                        deal?.kyc_verify_status ||
                        priorities[0]?.deal?.kyc_verify_status ||
                        'pending';
                      const kycLabel = evaluateKycVerification({
                        kyc_verify_status: kycStatus
                      }).label;

                      return (
                        <div
                          key={dealKey}
                          className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
                          <button
                            type="button"
                            className="flex w-full min-w-0 flex-nowrap items-center gap-2 overflow-x-auto px-4 py-3 text-left transition-colors hover:bg-gray-50 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                            title={`${blockUnit} — Customer: ${leadName} — Project: ${dealProject}`}
                            onClick={() => toggleDealExpanded(dealKey)}>
                            <div className="flex min-w-0 flex-1 items-center gap-x-2 text-sm leading-snug">
                              <span className="min-w-0 max-w-[40%] flex-[1.1] basis-0 truncate font-semibold text-gray-900">
                                {blockUnit}
                              </span>
                              <span className="shrink-0 text-gray-300" aria-hidden>
                                ·
                              </span>
                              <span className="hidden shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500 lg:inline">
                                Customer name
                              </span>
                              <span
                                className="min-w-0 flex-1 basis-0 truncate font-medium text-gray-800"
                                title={leadName}>
                                {leadName}
                              </span>
                              <span className="shrink-0 text-gray-300" aria-hidden>
                                ·
                              </span>
                              <span className="hidden shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500 lg:inline">
                                Project name
                              </span>
                              <span
                                className="min-w-0 flex-1 basis-0 truncate text-gray-700"
                                title={dealProject}>
                                {dealProject}
                              </span>
                            </div>
                            <div className="ml-auto flex shrink-0 items-center gap-1.5 whitespace-nowrap pl-2">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${kycVerifyBadgeClasses(
                                  kycStatus
                                )}`}
                                title="KYC number verification">
                                {kycLabel}
                              </span>
                              <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                                {priorities.length} stage{priorities.length !== 1 ? 's' : ''}
                              </span>
                              {pendingStages > 0 ? (
                                <span className="inline-flex rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-800">
                                  {pendingStages} pending
                                </span>
                              ) : null}
                              {dealTotal != null ? (
                                <span className="text-sm tabular-nums text-gray-600">
                                  <span className="font-medium text-gray-500">Deal total </span>
                                  <span className="font-semibold text-gray-900">₹{dealTotal}</span>
                                </span>
                              ) : null}
                              <ChevronDown
                                size={20}
                                className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                                aria-hidden
                              />
                            </div>
                          </button>
                          {open && (
                            <div className="border-t border-gray-200 bg-slate-50/60 px-3 py-4 space-y-4">
                              {numericDealId != null && (
                                <div className="flex flex-wrap justify-end gap-3">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setKycPanelDeal({
                                        id: numericDealId,
                                        label: `${blockUnit} · ${leadName}`
                                      });
                                    }}
                                    className="text-sm font-medium text-[#346C75] hover:underline">
                                    View KYC
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/builder/accounts/invoice/${numericDealId}`);
                                    }}
                                    className="text-sm font-medium text-[#346C75] hover:underline">
                                    Open full invoice workspace
                                  </button>
                                </div>
                              )}

                              <div>
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                  Payment stages
                                </h4>
                                <div className="overflow-x-auto rounded border border-gray-200 bg-white">
                                  <table className="text-left w-full min-w-[720px]">
                                    <thead className="bg-gray-100 text-gray-600 text-sm">
                                      <tr>
                                        <th className="px-3 py-2 font-normal">Title</th>
                                        <th className="px-3 py-2 font-normal">Stage</th>
                                        <th className="px-3 py-2 font-normal">Total</th>
                                        <th className="px-3 py-2 font-normal">Date</th>
                                        <th className="px-3 py-2 font-normal">Status</th>
                                        <th className="px-3 py-2 font-normal">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {priorities.map((priority, index) => (
                                        <tr key={priority.id || index} className="border-t border-gray-100 text-sm">
                                          <td className="px-3 py-2">
                                            <span className="font-medium">{priority.title || '---'}</span>
                                          </td>
                                          <td className="px-3 py-2">
                                            <div className="flex flex-col">
                                              <span className="font-medium">{priority.stageName || '---'}</span>
                                              <span className="text-xs text-gray-500">
                                                {priority.stagePercentage ? `${priority.stagePercentage}%` : '---'}
                                              </span>
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 font-medium text-green-700">
                                            ₹
                                            {priority.stageName === 'Management Task Expense'
                                              ? priority.amount
                                              : priority.deal?.total_price
                                                ? new Intl.NumberFormat('en-IN').format(priority.deal.total_price)
                                                : '---'}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {priority.date
                                              ? new Date(priority.date).toLocaleDateString('en-IN')
                                              : '---'}
                                          </td>
                                          <td className="px-3 py-2">
                                            <div className="relative z-0">
                                              <span className="flex pl-5 overflow-hidden text-sm">
                                                {priority.status || '---'}
                                              </span>
                                              <div
                                                className={`w-[4px] h-[100%] rounded-t-2xl rounded-r-[3rem] absolute left-[2px] top-0 ${priority.status === 'Completed'
                                                  ? 'bg-[#3F8D78]'
                                                  : priority.status === 'Pending'
                                                    ? 'bg-[#E97B37]'
                                                    : priority.status === 'In Progress'
                                                      ? 'bg-[#3B82F6]'
                                                      : 'bg-gray-400'
                                                  }`}>
                                                &nbsp;
                                              </div>
                                            </div>
                                          </td>
                                          <td className="px-3 py-2">
                                            <div className="flex flex-wrap gap-2">
                                              {priority.stageName === 'Management Task Expense' &&
                                              priority.status === 'Pending' ? (
                                                <button
                                                  type="button"
                                                  onClick={() => payExpense(priority.id)}
                                                  disabled={isPayingExpense}
                                                  className={`px-2 py-1 text-white text-xs rounded ${isPayingExpense
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-[#2a565e] hover:bg-[#1f4148]'
                                                    }`}>
                                                  {isPayingExpense ? '…' : 'Pay'}
                                                </button>
                                              ) : priority.status === 'Pending' ? (
                                                <button
                                                  type="button"
                                                  onClick={() => handleGenerateInvoice(priority)}
                                                  disabled={isGeneratingInvoice}
                                                  className={`px-2 py-1 text-white text-xs rounded ${isGeneratingInvoice
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-[#2a565e] hover:bg-[#1f4148]'
                                                    }`}>
                                                  {isGeneratingInvoice && selectedPriority?.id === priority.id
                                                    ? '…'
                                                    : 'Generate invoice'}
                                                </button>
                                              ) : null}
                                              {priority.status === 'Completed' && (
                                                <span className="text-xs text-gray-700">
                                                  {priority.stageName === 'Management Task Expense'
                                                    ? 'Paid'
                                                    : 'Invoice generated'}
                                                </span>
                                              )}
                                              {!['Pending', 'Completed'].includes(priority.status) && (
                                                <span className="text-gray-500 text-xs">—</span>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <PaginationComponent
                      page={page}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      isFetching={isLoading}
                    />
                  )}
                  </>
                  ) : (
                    <div className="w-full px-2 py-6">
                      <NoDataFound
                        buttonText="Refresh"
                        onButtonClick={() => refetchPriorities()}
                        screenHeight={false}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </GridWrapper>
      </Layout>

      {kycPanelDeal?.id != null && (
        <AccountsKycPanel
          dealId={kycPanelDeal.id}
          dealLabel={kycPanelDeal.label}
          onClose={() => setKycPanelDeal(null)}
        />
      )}

      {/* Invoice Generation Modal */}
      {isInvoiceModalOpen && (
        <Modal name="Generate Invoice" onClose={handleCloseInvoiceModal} width={40} isDirty={false}>
          <div>
            <div className="mb-4">
              <p className="text-xs text-gray-500 mt-1">
                Lead: {selectedPriority?.deal?.lead?.user?.name} | Unit:{' '}
                {selectedPriority?.deal?.unit?.unitName}
              </p>
              {selectedPriority?.deal?.kyc_verify_status && (
                <span
                  className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${kycVerifyBadgeClasses(
                    selectedPriority.deal.kyc_verify_status
                  )}`}>
                  {
                    evaluateKycVerification({
                      kyc_verify_status: selectedPriority.deal.kyc_verify_status
                    }).label
                  }
                </span>
              )}
            </div>

            <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">Previous invoices (this deal)</p>
              {isLoadingDealInvoices && (
                <p className="text-xs text-gray-500">Loading invoice history…</p>
              )}
              {isDealInvoicesError && !isLoadingDealInvoices && (
                <p className="text-xs text-amber-700">Could not load previous invoices.</p>
              )}
              {!isLoadingDealInvoices && !isDealInvoicesError && previousInvoices.length === 0 && (
                <p className="text-xs text-gray-500">No previous invoices for this deal yet.</p>
              )}
              {!isLoadingDealInvoices && previousInvoices.length > 0 && (
                <div className="max-h-40 overflow-y-auto text-xs">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-200">
                        <th className="py-1 pr-2 font-normal">Invoice #</th>
                        <th className="py-1 pr-2 font-normal">Source</th>
                        <th className="py-1 pr-2 font-normal text-right">Amount</th>
                        <th className="py-1 font-normal">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previousInvoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-gray-100 last:border-0">
                          <td className="py-1.5 pr-2 text-gray-800 font-medium">
                            {inv.invoiceNumber || '—'}
                          </td>
                          <td className="py-1.5 pr-2 text-gray-600">{inv.source || '—'}</td>
                          <td className="py-1.5 pr-2 text-right text-gray-800">
                            ₹{new Intl.NumberFormat('en-IN').format(Number(inv.amount) || 0)}
                          </td>
                          <td className="py-1.5 text-gray-600">{inv.status || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit(onSubmitInvoice)} className="space-y-4">
              <div>
                <FormInput
                  label="Invoice Number"
                  isRequired
                  register_key="invoiceNumber"
                  register={register}
                  type="text"
                  placeholder="Enter invoice number"
                  errors={errors.invoiceNumber}
                  width={100}
                />
              </div>

              <div>
                <FormInput
                  label="Issue Date"
                  isRequired
                  register_key="issueDate"
                  register={register}
                  type="date"
                  errors={errors.issueDate}
                  width={100}
                  CustomFeature={{
                    max: new Date().toISOString().split('T')[0]
                  }}
                />
              </div>

              <div>
                <FormInput
                  label="Invoice Type"
                  isRequired
                  register_key="invoiceType"
                  register={register}
                  type="dropdown"
                  options={[
                    { label: 'Company Based', value: 'Company Based' },
                    { label: 'Owner Based', value: 'Owner Based' }
                  ]}
                  errors={errors.invoiceType}
                  width={100}
                />
              </div>

              {invoiceType === 'Owner Based' && (
                <div>
                  {loadingLandOwners ? (
                    <div className="text-sm text-gray-500">Loading land owners...</div>
                  ) : (
                    <FormInput
                      label="Land Owner"
                      isRequired
                      register_key="landOwner"
                      register={register}
                      type="dropdown"
                      options={[
                        { label: 'Select Land Owner', value: '' },
                        ...landOwners
                      ]}
                      errors={errors.landOwner}
                      width={100}
                    />
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={handleCloseInvoiceModal}
                  displayType="secondary"
                  disabled={isGeneratingInvoice}>
                  Cancel
                </Button>
                <Button type="submit" displayType="primary" disabled={isGeneratingInvoice}>
                  {isGeneratingInvoice ? 'Generating...' : 'Generate Invoice'}
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Priorities;
